import { Pencil, Archive, Trash2, Megaphone } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { EmptyState } from "@/shared/components/EmptyState";
import { RowActionMenu, type RowAction } from "@/shared/components/RowActionMenu";
import { Skeleton } from "@/shared/ui/skeleton";

import { formatPeriod } from "../lib/format-period";

import { CreatedBy } from "./CreatedBy";
import { StatusCountPills } from "./StatusCountPills";

type Campaign = components["schemas"]["Campaign"];

interface CampaignTableProps {
  items: Campaign[];
  isLoading: boolean;
  isError?: boolean;
  onRowClick: (campaign: Campaign) => void;
  onRename: (campaign: Campaign) => void;
  onArchive: (campaign: Campaign) => void;
  onDelete: (campaign: Campaign) => void;
  /** promotions:manage — when false the row action menu is not rendered. */
  canManage?: boolean;
}

const TH =
  "h-9 px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap select-none";
const TD = "px-4 py-3 text-sm text-[var(--text-primary)] align-middle";

export function CampaignTable({
  items,
  isLoading,
  isError,
  onRowClick,
  onRename,
  onArchive,
  onDelete,
  canManage = true,
}: CampaignTableProps): JSX.Element {
  const { t } = useTranslation("campaign");

  if (isError) {
    return (
      <div className="rounded-2xl border border-[var(--premium-border)] bg-white">
        <EmptyState title={t("empty.title")} body={t("empty.body")} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="space-y-2 rounded-2xl border border-[var(--premium-border)] bg-white p-4"
        data-testid="campaign-table-loading"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className="rounded-2xl border border-[var(--premium-border)] bg-white"
        data-testid="campaign-table"
      >
        <EmptyState
          icon={<Megaphone className="h-5 w-5" aria-hidden />}
          title={t("empty.title")}
          body={t("empty.body")}
        />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-[var(--premium-border)] bg-white">
      <table className="w-full border-collapse" data-testid="campaign-table">
        <thead>
          <tr className="border-b border-[var(--border-subtle)]">
            <th className={`${TH} min-w-[240px]`}>{t("column.name")}</th>
            <th className={`${TH} min-w-[150px]`}>{t("column.period")}</th>
            <th className={`${TH} min-w-[220px]`}>{t("column.status")}</th>
            <th className={`${TH} min-w-[180px]`}>{t("column.created_by")}</th>
            <th className={`${TH} w-[52px] text-right`} aria-label={t("column.actions")} />
          </tr>
        </thead>
        <tbody>
          {items.map((c) => {
            const actions: RowAction[] = [
              {
                key: "rename",
                label: t("action.rename"),
                icon: <Pencil className="h-4 w-4" aria-hidden />,
                onSelect: () => onRename(c),
              },
              {
                key: "archive",
                label: t("action.archive"),
                icon: <Archive className="h-4 w-4" aria-hidden />,
                onSelect: () => onArchive(c),
              },
              {
                key: "delete",
                label: t("action.delete"),
                icon: <Trash2 className="h-4 w-4" aria-hidden />,
                danger: true,
                onSelect: () => onDelete(c),
              },
            ];
            return (
              <tr
                key={c.id}
                data-testid="campaign-row"
                className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--canvas)]"
              >
                <td className={TD}>
                  <button
                    type="button"
                    data-testid="campaign-name-link"
                    onClick={() => onRowClick(c)}
                    className="font-semibold text-[var(--text-primary)] hover:text-[var(--brand-500)] focus-ring rounded"
                  >
                    {c.name}
                  </button>
                </td>
                <td
                  className={`${TD} whitespace-nowrap tabular-nums text-[13px] text-[var(--text-secondary)]`}
                >
                  {formatPeriod(c.period_start, c.period_end)}
                </td>
                <td className={TD}>
                  <StatusCountPills rollup={c.status_rollup} />
                </td>
                <td className={TD}>
                  <CreatedBy createdBy={c.created_by} createdByName={c.created_by_name} />
                </td>
                <td className={`${TD} text-right`}>
                  {canManage && <RowActionMenu actions={actions} label={t("column.actions")} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
